import { useRef, useState } from 'react'

import { Alert, Box, Button, Icon, Text } from '@factorialco/deprecated-design-system'
import { RichTextEditorHandle } from '@factorialco/f0-react/dist/experimental'
import clouds from 'assets/images/import-clouds.svg'
import {
  AtsCreateOfferLetterDocument,
  AtsJobApplicationFilesOfferLetterDocument,
  AtsSendOfferLetterModalApplicationFragment,
  AtsSendOfferLetterModalGetOfferLettersDocument,
} from 'generated/resources/graphql'

import { Authorized } from 'modules/core/components/Authorized'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { acceptedContentTypes } from 'modules/core/lib/fileFormats'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { TextEditorWithMessageTemplates } from 'modules/ats/components/TextEditorWithMessageTemplates'
import { Statuses } from 'modules/ats/lib/enums'
import DropzoneBox from 'modules/documents/components/Documents/DropzoneBox'
import FileList from 'modules/documents/components/FileList'

interface Props {
  application: AtsSendOfferLetterModalApplicationFragment
  onClose: () => void
}

export const OfferLetterFromScratchModal = ({ application, onClose }: Props) => {
  const {
    company: { subscription },
    access,
  } = useCurrentSession()

  const richTextEditorRef = useRef<RichTextEditorHandle>(null)

  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [customMessage, setCustomMessage] = useState<string>('')

  const { data: applicationOfferLetter } = useQuery(
    AtsSendOfferLetterModalGetOfferLettersDocument,
    {
      variables: { applicationId: application.id },
    }
  )

  const ACCEPTED_CONTENT_TYPES = acceptedContentTypes([
    'application/pdf',
    'application/doc',
    'application/docx',
  ])

  const [createOfferLetter, { loading: sendingOfferLetter }] = useMutation(
    AtsCreateOfferLetterDocument,
    {
      extractorFn: (data) => data.atsMutations.createAtsOfferLetter,
      refetchQueries: [AtsJobApplicationFilesOfferLetterDocument],
    }
  )

  const onSendOfferLetter = async () => {
    const result = await createOfferLetter({
      variables: {
        accessId: access.id,
        atsApplicationId: application.id,
        customMessage: customMessage,
        document: selectedFiles[0],
        status: Statuses.Pending,
      },
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('ats.offer_letter.modal.success_message'),
      })

      onClose()
    }

    return handled
  }

  const onDrop = (files: File[]) => {
    setSelectedFiles(selectedFiles.concat(files))
  }

  const onRemoveFile = (indexToRemove: number) => {
    setSelectedFiles(selectedFiles.filter((_file, index) => index !== indexToRemove))
  }

  const requiredData = !!customMessage && !!selectedFiles.length

  if (!application.candidate.email) {
    return (
      <Modal
        illustration="alert"
        title={i18n.t('ats.offer_letter.modal.no_email.title')}
        description={i18n.t('ats.offer_letter.modal.no_email.description')}
        onClose={onClose}
      />
    )
  }

  return (
    <Modal
      illustration="document-edit"
      title={i18n.t('ats.offer_letter.modal.title')}
      description={i18n.t('ats.offer_letter.modal.description')}
      onSubmit={onSendOfferLetter}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={sendingOfferLetter || !requiredData} submit>
            {i18n.t('ats.offer_letter.modal.submit')}
          </Button>
        </>
      }
    >
      <Box gap="s16">
        <Alert color="warning">
          {applicationOfferLetter ? (
            <Box alignItems="flexStart" flexDirection="row" gap="s4">
              <Box flexNoShrink>
                <Icon.Medium icon="alert-circle" color="grey800" />
              </Box>
              <Text.Normal>
                {i18n.t('ats.offer_letter.modal.warning_message_existing_offer')}
              </Text.Normal>
            </Box>
          ) : (
            <Text.Normal>{i18n.t('ats.offer_letter.modal.warning_message')}</Text.Normal>
          )}
        </Alert>

        {selectedFiles.length ? (
          <FileList files={selectedFiles} onDeleteFile={onRemoveFile} />
        ) : (
          <Authorized subscription={subscription}>
            <DropzoneBox
              onDrop={onDrop}
              icon={clouds}
              multiple={false}
              message={i18n.t('documents.drop_here')}
              accept={ACCEPTED_CONTENT_TYPES}
            />
          </Authorized>
        )}

        <TextEditorWithMessageTemplates
          richTextEditorRef={richTextEditorRef}
          onChange={(value) => setCustomMessage(value || '')}
          showLabel
          placeholder={i18n.t('ats.offer_letter.modal.custom_message_placeholder', {
            candidate_name: application.candidate.fullName,
          })}
          value={customMessage}
        />
      </Box>
    </Modal>
  )
}
